import { useMemo, useState } from 'react'
import ChampCard from './ChampCard.jsx'
import { daysToEvent, LOOKBACK_DAYS, makeComparator } from '../../../lib/championships.js'
import { SortBar } from './SortControls.jsx'
import styles from './pursuit.module.css'

// Cards per lane before the lane offers the rest. A page, not a cap.
const PAGE = 120

// 'recent' keeps the historic default (most-recently-concluded first); every
// other key runs through the shared comparator, so Owner sorts identically here.
const SORT_FIELDS = [
  { key: 'recent', label: 'Most recent' },
  { key: 'owner', label: 'Representative' },
  { key: 'name', label: 'Championship' },
  { key: 'date', label: 'Event date' },
  { key: 'sport', label: 'Sport' },
]

// Stable component identity preserves cards and keyboard focus across policy refreshes.
function Lane({ id, title, hint, list, empty, limits, bump, now, onOpen }) {
  const limit = limits[id] ?? PAGE
  const shown = limit >= list.length ? list : list.slice(0, limit)
  return (
    <section>
      <div className={styles.laneHead}>
        <span className={styles.laneTitle}>{title}</span>
        <span className={styles.laneCount}>{list.length.toLocaleString()}</span>
        <span className={styles.laneHint}>{hint}</span>
        {list.length > shown.length && (
          <span className={styles.groupShowing}>showing {shown.length.toLocaleString()} of {list.length.toLocaleString()}</span>
        )}
      </div>
      {list.length ? (
        <>
          <div className={styles.cardGrid}>
            {shown.map((c) => <ChampCard key={c.id} c={c} now={now} onOpen={onOpen} />)}
          </div>
          {list.length > shown.length && (
            <div className={styles.more}>
              <button className={styles.moreBtn} onClick={() => bump(id, limit + PAGE * 4)}>
                Show {Math.min(PAGE * 4, list.length - shown.length).toLocaleString()} more
              </button>
              <button className={styles.moreBtn} onClick={() => bump(id, list.length)}>
                Show all {list.length.toLocaleString()}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>{empty}</div>
      )}
    </section>
  )
}

/**
 * Champions crowned OR events just concluded — the reorder / win-back lane.
 * "Just concluded" is the rolling look-back window (last 90 days), keyed off
 * the effective date, so a two-year-old event never lands here.
 */
export default function JustWon({ items, now, onOpen }) {
  const [limits, setLimits] = useState({ crowned: PAGE, concluded: PAGE })
  const [sort, setSort] = useState({ field: 'recent', dir: 'asc' })
  const bump = (k, n) => setLimits((s) => ({ ...s, [k]: n }))

  /* ⚠️ "JUST WON" IS A DATE WINDOW, NOT "HAS A CHAMPION".
   *
   * The crowned lane was `if (c.crowned || c.champion)` with no date bound at
   * all — the same `if (c.crowned)` shape v1.13 removed from Stages, left in
   * place here. So the top card, in a view whose own copy reads "crowned in
   * the last stretch… this is where a ring conversation starts", sorted by
   * Most recent, was:
   *
   *     PGF National Championships · 14U Premier · Softball
   *     Jun 5, 2027 · in 267d · champion "LTG 2029-Henderson/Buck"
   *
   * An event 267 days in the FUTURE, presented as recently won.
   *
   * Both lanes are now bounded by the same rolling look-back window. But a
   * crowned record outside that window is not hidden — it is the stale-champion
   * finding (a 2029 season tag on a 2027-dated record), and burying it would
   * delete the evidence. It gets its own lane that says what it is. */
  const inWindow = (c) => {
    const d = daysToEvent(c, now)
    return d != null && d >= -LOOKBACK_DAYS && d <= 0
  }
  const { crowned, concluded, offWindow } = useMemo(() => {
    const cr = [], co = [], off = []
    for (const c of items) {
      const hasChampion = c.crowned || c.champion
      if (hasChampion) (inWindow(c) ? cr : off).push(c)
      else if (inWindow(c)) co.push(c)
    }
    const mul = sort.dir === 'desc' ? -1 : 1
    const byRecent = (a, b) => {
      const da = daysToEvent(a, now) ?? -9999, db = daysToEvent(b, now) ?? -9999
      return (db - da) * mul
    }
    const cmp = sort.field === 'recent' ? byRecent : makeComparator(sort.field, sort.dir, now)
    return { crowned: cr.sort(cmp), concluded: co.sort(cmp), offWindow: off.sort(cmp) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, now, sort])

  return (
    <div className={styles.justwon}>
      <SortBar sort={sort} setSort={setSort} fields={SORT_FIELDS} label="Sort cards by" />
      <Lane
        limits={limits} bump={bump} now={now} onOpen={onOpen}
        id="crowned"
        title="Champions crowned"
        hint="reorder & win-back — the ring is on the table"
        list={crowned}
        empty="No captured champions yet (~1% of records carry a winner)."
      />
      <Lane
        limits={limits} bump={bump} now={now} onOpen={onOpen}
        id="concluded"
        title="Just concluded"
        hint={`event ended in the last ${LOOKBACK_DAYS} days — capture the result`}
        list={concluded}
        empty={`Nothing concluded in the last ${LOOKBACK_DAYS} days.`}
      />
      {/* NOT A RING CONVERSATION — A DATA ONE. These carry a champion but are
          dated outside the look-back window, most of them in the FUTURE. They
          used to sit at the top of "Champions crowned" as if they had just been
          won. Shown, because a champion on an unplayed event is a finding. */}
      {offWindow.length > 0 && (
        <Lane
          limits={limits} bump={bump} now={now} onOpen={onOpen}
          id="offwindow"
          title="Champion recorded, but dated outside the window"
          hint={`not won in the last ${LOOKBACK_DAYS} days — a future or long-past date carrying a champion. Check the date and the season before quoting.`}
          list={offWindow}
          empty=""
        />
      )}
    </div>
  )
}
